<template>
  <el-tabs v-model="activeTab" class="instagram-analytics-public-tabs">
    <el-tab-pane label="キャンペーン" name="campaigns">
      <div class="graph-container">
        <InstagramInstantwinAnalyticsPublicCampaign />
      </div>
    </el-tab-pane>
    <el-tab-pane label="アンケート" name="questionnaire">
      <div class="graph-container">
        <InstagramInstantwinCampaignAnalyticsEnqueteTab
          :prize-id="prizeId"
          :campaign-name="campaignName"
          :questions="enqueteQuestions"
          :selected-id="enqueteSelectedId"
          :answers="enqueteAnswers"
          :total-count="enqueteTotalCount"
          :loading="enqueteLoading"
          @select-question="handleEnqueteQuestionChange"
        />
      </div>
    </el-tab-pane>
    <el-tab-pane label="メッセージごとの送信数" name="message_send_counts">
      <div class="graph-container">
        <InstagramInstantwinCampaignAnalyticsMessageSendCountsTab
          :prize-id="prizeId"
          :rows="messageSendCountRows"
          :total="messageSendCountTotal"
          :paging-no="messageSendCountPagingNo"
          :limit-count="messageSendCountLimitCount"
          :sort-by="messageSendCountSortBy"
          :loading="messageSendCountLoading"
          :download-csv="downloadMessageSendCountsCsv"
          @sort-change="handleMessageSendCountsSort"
          @paging="handleMessageSendCountsPaging"
        />
      </div>
    </el-tab-pane>
  </el-tabs>
</template>

<script lang="ts">
import { storeToRefs } from 'pinia'
import { computed, defineComponent, ref, watch } from 'vue'

import { useInstagramInstantwinAnalyticsPublicStore } from '@/client/features/instagram_instantwin_analytics_public/hooks/instagramInstantwinAnalyticsPublicStore'
import InstagramInstantwinCampaignAnalyticsEnqueteTab from '@/client/features/instagram_instantwin_campaign_analytics/components/InstagramInstantwinCampaignAnalyticsEnqueteTab.vue'
import InstagramInstantwinCampaignAnalyticsMessageSendCountsTab from '@/client/features/instagram_instantwin_campaign_analytics/components/InstagramInstantwinCampaignAnalyticsMessageSendCountsTab.vue'
import { MessageSendCountSortBy } from '@/client/features/instagram_instantwin_campaign_analytics/types'

import InstagramInstantwinAnalyticsPublicCampaign from '../instagram_instantwin_analytics_public_campaign'

export default defineComponent({
  components: {
    InstagramInstantwinAnalyticsPublicCampaign,
    InstagramInstantwinCampaignAnalyticsEnqueteTab,
    InstagramInstantwinCampaignAnalyticsMessageSendCountsTab
  },
  setup() {
    const store = useInstagramInstantwinAnalyticsPublicStore()
    const {
      params,
      campaign,
      enqueteQuestions,
      enqueteSelectedId,
      enqueteAnswers,
      enqueteTotalCount,
      messageSendCountRows,
      messageSendCountTotal,
      messageSendCountPagingNo,
      messageSendCountLimitCount,
      messageSendCountSortBy,
      enqueteLoading,
      messageSendCountLoading
    } = storeToRefs(store)

    const activeTab = ref('campaigns')

    const prizeId = computed(() => {
      const raw = params.value.prizeId
      if (!raw) {
        return null
      }
      const value = Number(raw)
      return Number.isFinite(value) && value > 0 ? value : null
    })

    const campaignName = computed(() => campaign.value?.title ?? '')

    const refreshByTab = async (tabName: string) => {
      if (tabName === 'questionnaire') {
        await store.fetchEnqueteAnalytics()
      }
      if (tabName === 'message_send_counts') {
        await store.fetchMessageSendCounts()
      }
    }

    watch(activeTab, value => {
      void refreshByTab(value)
    })

    watch(
      () => [params.value.prizeId, params.value.startDatetime, params.value.endDatetime],
      () => {
        void refreshByTab(activeTab.value)
      }
    )

    const handleEnqueteQuestionChange = async (value: number) => {
      store.setEnqueteSelectedId(value)
      await store.fetchEnqueteAnalytics()
    }

    const handleMessageSendCountsSort = async (sortBy: MessageSendCountSortBy | '') => {
      store.setMessageSendCountSortBy(sortBy)
      await store.fetchMessageSendCounts()
    }

    const handleMessageSendCountsPaging = async ({
      currentPage,
      pageSize
    }: {
      currentPage: number
      pageSize: number
    }) => {
      store.setMessageSendCountPaging(currentPage, pageSize)
      await store.fetchMessageSendCounts()
    }

    const downloadMessageSendCountsCsv = async () => {
      if (!prizeId.value) {
        return { error: 'NO_DATA' }
      }

      const payload = await store.exportMessageSendCountsCsv()
      const rows = payload?.rows ?? []
      if (!rows.length) {
        return { error: 'NO_DATA' }
      }

      const data: Array<Array<string | number | null>> = []
      data.push(['メッセージ', '種別', '送信数'])
      rows.forEach(row => {
        data.push([row.message || '', row.messageType, row.receivedCount])
      })

      const filename = campaignName.value ? `${campaignName.value}_メッセージ送信数` : 'メッセージ送信数'
      return { filename, data }
    }

    return {
      activeTab,
      prizeId,
      campaignName,
      enqueteQuestions,
      enqueteSelectedId,
      enqueteAnswers,
      enqueteTotalCount,
      messageSendCountRows,
      messageSendCountTotal,
      messageSendCountPagingNo,
      messageSendCountLimitCount,
      messageSendCountSortBy,
      enqueteLoading,
      messageSendCountLoading,
      handleEnqueteQuestionChange,
      handleMessageSendCountsSort,
      handleMessageSendCountsPaging,
      downloadMessageSendCountsCsv
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.instagram-analytics-public-tabs {
  :deep(.el-tabs__content) {
    max-height: none;
  }
}

.graph-container {
  padding: $spacing-unit ($spacing-unit * 3);
  border: 1px solid $border-color;
}
</style>
